import axiosClient from '@/lib/utils/axiosClient';
import {
  loadMyUser,
  myUserPath,
  pickUserRecord,
  type CompanyPreferences,
  type MyUserRecord,
  type PersonalSettingsPayload,
  type ProfilePatch,
  type ProfilePhotoPutBody
} from '@/lib/api/my-account';
import { CUSTOMIZATION } from '@/types';

/** Company dashboard colour; read by `ThemeInitializer`. Reps never write it. */
export async function fetchCustomizationDetails(): Promise<CUSTOMIZATION> {
  const res = await axiosClient.get(`company/dashboard/customizations`);
  return res.data;
}

/** Shared by Preferences and the shell's user menu avatar. */
export const myUserQueryKey = (userId: string) => ['my-user', userId] as const;
/** `useUserData`'s key (the sidebar's name); invalidated after a save. */
export const sidebarUserQueryKey = (userId: string) =>
  ['user', userId] as const;
export const COMPANY_PREFERENCES_QUERY_KEY = ['company-preferences'] as const;

/**
 * The signed-in user's own record: glp's self-service `GET users/?id={me}`
 * row (own id only), with the photo taken from `GET users/{me}` when that row
 * has none. See `loadMyUser`.
 */
export async function fetchMyUser(userId: string): Promise<MyUserRecord> {
  return loadMyUser(async (path) => (await axiosClient.get(path)).data, userId);
}

/**
 * `GET company/preferences` — read only, for the "Use company default (…)"
 * labels. glp-frontend reads it for every signed-in user; only its admin
 * screen writes it.
 */
export async function fetchCompanyPreferences(): Promise<CompanyPreferences> {
  const { data } = await axiosClient.get<CompanyPreferences>(
    'company/preferences'
  );
  return data ?? {};
}

/**
 * `PATCH users/{me}/` — glp-frontend's self-service write (My Settings). PATCH
 * so only the fields this page owns are sent; the id is always the session's.
 */
export async function patchMyUser(
  userId: string,
  body: PersonalSettingsPayload | ProfilePatch
): Promise<MyUserRecord> {
  const { data } = await axiosClient.patch(myUserPath(userId), body);
  return pickUserRecord(data);
}

/**
 * `PUT users/{me}/` — glp's "Your own details" save (`UserProfile.jsx`), used
 * only when a new photo is saved (see `buildProfilePhotoPutBody`). The id is
 * always the session's.
 */
export async function putMyUser(
  userId: string,
  body: ProfilePhotoPutBody
): Promise<MyUserRecord> {
  const { data } = await axiosClient.put(myUserPath(userId), body);
  return pickUserRecord(data);
}
